import { FC, useMemo, useState } from "react"
import { Building2, AlertTriangle, Info } from "lucide-react"
import { Brand, LoanOfficer } from "../../../../types"
import BrandAutocomplete from "../../brand-autocomplete"
import LoanOfficerAutocomplete from "../../loan-officer-autocomplete"
import { PrimaryBtn } from "../../buttons"
import Dialog from "../../dialog"
import loTransfer from "../../../../api/lo-transfer"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"
import ConfigToolCard from "./config-tool-card"

interface Props {
  brands: Brand[]
}

const LoTransferBrand: FC<Props> = ({ brands }) => {
  const dispatch = useAppDispatch()
  const [currentBrandId, setCurrentBrandId] = useState<string>("")
  const [currentBrandName, setCurrentBrandName] = useState<string>("")
  const [loid, setLoid] = useState<string>("")
  const [loDisabled, setLoDisabled] = useState(false)
  const [assetRecipientLoid, setAssetRecipientLoid] = useState<string>("")
  const [recipientDisabled, setRecipientDisabled] = useState(false)
  const [newBrandId, setNewBrandId] = useState<string>("")
  const [isLoading, setIsLoading] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const canTransfer =
    currentBrandId &&
    loid &&
    assetRecipientLoid &&
    newBrandId &&
    currentBrandId !== newBrandId &&
    loid !== assetRecipientLoid

  const handleTransfer = async () => {
    setShowConfirm(false)
    setIsLoading(true)

    try {
      // Re-enable disabled LOs before transferring
      if (loDisabled) {
        await loTransfer.undeleteLo({ loid: parseInt(loid, 10) })
      }
      if (recipientDisabled) {
        await loTransfer.undeleteLo({
          loid: parseInt(assetRecipientLoid, 10)
        })
      }

      // Step 1: Transfer assets to recipient LO
      await loTransfer.transferAssets({
        from_loid: parseInt(loid, 10),
        to_loid: parseInt(assetRecipientLoid, 10)
      })

      // Step 2: Transfer LO to new brand
      await loTransfer.transferBrand({
        loid: parseInt(loid, 10),
        new_brand_id: parseInt(newBrandId, 10)
      })

      dispatch(
        pushToast({
          msg: "Assets transferred and loan officer moved to new brand successfully",
          theme: "success",
          timeout: 4000
        })
      )

      // Reset form
      setCurrentBrandId("")
      setCurrentBrandName("")
      setLoid("")
      setLoDisabled(false)
      setAssetRecipientLoid("")
      setRecipientDisabled(false)
      setNewBrandId("")
    } catch (error: any) {
      dispatch(
        pushToast({
          msg: error?.message || "Failed to complete transfer",
          theme: "danger",
          timeout: 5000
        })
      )
    } finally {
      setIsLoading(false)
    }
  }

  const handleLoSelect = (lo: LoanOfficer) => {
    setCurrentBrandId(lo.bank_nid)
    setLoDisabled(lo.active === false)
    // Look up brand name from brands list since API doesn't always return it
    const brand = brands.find(b => b.id === lo.bank_nid)
    setCurrentBrandName(brand?.name ?? lo.brand_name ?? "")
    setAssetRecipientLoid("")
    setRecipientDisabled(false)
    setNewBrandId("")
  }

  const handleLoidChange = (val: string) => {
    setLoid(val)
    if (!val) {
      setCurrentBrandId("")
      setCurrentBrandName("")
      setLoDisabled(false)
    }
    setAssetRecipientLoid("")
    setRecipientDisabled(false)
    setNewBrandId("")
  }

  const handleRecipientSelect = (lo: LoanOfficer) => {
    setRecipientDisabled(lo.active === false)
  }

  const targetBrand = brands.find(b => b.id === newBrandId)

  const confirmBody = useMemo(() => {
    const parts = [
      `This will first transfer all assets to the selected loan officer, then move the departing LO from "${
        currentBrandName || "current brand"
      }" to "${targetBrand?.name || "new brand"}".`
    ]
    if (loDisabled || recipientDisabled) {
      const who =
        loDisabled && recipientDisabled
          ? "Both loan officers are"
          : loDisabled
          ? "The departing loan officer is"
          : "The asset recipient is"
      parts.push(`${who} currently disabled and will be re-enabled.`)
    }
    parts.push("This cannot be undone.")
    return parts.join(" ")
  }, [currentBrandName, targetBrand?.name, loDisabled, recipientDisabled])

  return (
    <ConfigToolCard
      icon={<Building2 className="w-5 h-5 text-indigo-600" />}
      iconClassName="bg-indigo-100"
      title="LO Brand Transfer"
      description="Move a loan officer to a different brand"
    >
      <div className="p-6 space-y-5">
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">
              Assets must stay with the current brand
            </p>
            <p className="mt-1 text-amber-700">
              Before transferring, all assets (leads, SMS threads, etc.) will be
              reassigned to another loan officer in the current brand.
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Loan Officer to Transfer:
            </label>
            <LoanOfficerAutocomplete
              searchParams={{ include_disabled: 1 }}
              selected={loid}
              label=""
              direction="vertical"
              showBrandName
              showSearchToggle
              onChange={handleLoidChange}
              onSelectLo={handleLoSelect}
            />
            {loDisabled && (
              <div className="mt-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
                <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  This loan officer is currently disabled and will be re-enabled
                  when you transfer.
                </p>
              </div>
            )}
          </div>

          {currentBrandId && (
            <>
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Current Brand:</span>{" "}
                  {currentBrandName}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Transfer Assets To:
                </label>
                <LoanOfficerAutocomplete
                  searchParams={{
                    brand_id: currentBrandId,
                    include_disabled: 1
                  }}
                  selected={assetRecipientLoid}
                  label=""
                  direction="vertical"
                  excludeIds={[loid]}
                  onChange={setAssetRecipientLoid}
                  onSelectLo={handleRecipientSelect}
                />
                <p className="mt-1 text-xs text-gray-500">
                  This LO will receive all assets from the departing loan
                  officer
                </p>
                {recipientDisabled && (
                  <div className="mt-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg flex gap-2">
                    <Info className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-blue-700">
                      This loan officer is currently disabled and will be
                      re-enabled when you transfer.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {assetRecipientLoid && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                New Brand:
              </label>
              <BrandAutocomplete
                selected={newBrandId}
                brands={brands.filter(b => b.id !== currentBrandId)}
                direction="vertical"
                hideLabel
                onChange={setNewBrandId}
              />
            </div>
          )}
        </div>

        <div className="pt-2">
          <PrimaryBtn
            onClick={() => setShowConfirm(true)}
            disabled={!canTransfer || isLoading}
            className="w-full justify-center"
          >
            {isLoading ? "Transferring..." : "Transfer to Brand"}
          </PrimaryBtn>
        </div>
      </div>

      <Dialog
        open={showConfirm}
        title="Confirm Brand Transfer"
        body={confirmBody}
        okLabel="Yes, Transfer"
        cancelLabel="Cancel"
        onOk={handleTransfer}
        onCancel={() => setShowConfirm(false)}
        onClose={() => setShowConfirm(false)}
        theme="warning"
      />
    </ConfigToolCard>
  )
}

export default LoTransferBrand
